import { createRef, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Link from "next/link";
import { usePathname } from "next/navigation";
import useRouter from "@stadium/hooks/lib/use-custom-route";

import { useStoreContext } from "@components/store/provider";
import { useAuth0V2 } from "@stadium/services/auth0";
import { useFlags } from "@stadium/services/launch-darkly";

import { getIsPricelessShop, getIsSinglePageShop } from "@selectors/store";
import { getReceivedItemsCount } from "@selectors/cart";

import CheckoutProvider from "@contexts/checkout-context";
import { useCreateShopContext } from "@contexts/create-shop-context";
import { useSendItemsContext } from "@contexts/send-items-context";
import { useWorkspaceContext } from "@contexts/workspace-context";

import GlobalProductSearch from "@sharedComponents/global-new-search/global-product-search";
import { ExternalButton } from "@sharedComponents/buttons";
import MultiLevelMenu from "@components/store/multi-level-menu/responsive";
import SliderContainer from "@stadium/ui/src/components/SliderDropdown/container";
import { Spinner } from "@stadium/ui/src/components/loaders/loaders";

import useFetchExternalStore from "@hooks/use-fetch-external-store";

import useHeaderHeight from "@stadium/ui/src/components/header/use-header-height";
import AppGenericAnnouncementBar from "@stadium/ui/src/components/app-generic-announcement-bar";
import LogoutButton from "@components/auth/buttons/logout";
import UnAuthtenticatedRightSection from "@components/store/navigation/unauthenticated-right-section";
import { CountrySelector } from "@components/store/navigation/right-section";
import CartIcon from "@components/store/navigation/cart-icon";
import UserPointsBalance from "@components/store/navigation/header-points-balance";
import Profile from "@components/store/navigation/ProfileDropdown/responsive";
import CreateShopBanner from "@components/store/navigation/create-shop-banner";
import NavSlider from "@components/store/navigation/nav-slider";
import MobileMenuIcon from "@components/store/navigation/mobile-menu-icon";
import BrandLogo from "@components/store/navigation/brand-logo";
import SendItemsSelection from "@components/store/navigation/SendItemsSelection";
import BottomIconHideWrapper from "@stadium/shared/src/components/bottom-icon-hide-wrapper";
import SelectedItemsCount from "@components/store/navigation/selected-items-count";
import TalkToSalesButton from "@components/talk-to-sales-button";

import {
  ResponsiveHeaderContent,
  HeaderContent,
  STICKY_HEADER_REF,
} from "@stadium/ui/src/components/header";

import { HELP_CENTER_URL } from "@stadium/constants/routes";
import { SHOPS } from "@stadium/constants";
import { workspaceSidebarFlag } from "@stadium/services/launch-darkly/constants";
import { HEADER_ANALYTICS_TARGET } from "@stadium/shared/src/constants/analytics/header";

import styles from "./main.module.scss";

const FIRST_HEADER_REF = createRef();

const DEFAULT_HEADER_HEIGHT = 118;

export const getResponsiveHeaderHeight = (isMobileNavOpen, headerHeight) => {
  return `${
    isMobileNavOpen
      ? FIRST_HEADER_REF.current?.clientHeight
      : headerHeight || STICKY_HEADER_REF.current?.clientHeight
  }px`;
};

const ResponsiveMultiLevelHeader = ({ isFetchingMenu }) => {
  const { [workspaceSidebarFlag]: workspaceSidebarFlagEnabled } = useFlags();

  const headerHeight = useHeaderHeight(DEFAULT_HEADER_HEIGHT);

  const isPricelessShop = useSelector(getIsPricelessShop);
  const isSinglePageShop = useSelector(getIsSinglePageShop);
  const receivedItemsCount = useSelector(getReceivedItemsCount);

  const pathname = usePathname();
  const router = useRouter();
  const { productSlug } = router.query;

  const isProductDetailPage = Boolean(productSlug);

  const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);

  const {
    withMenu,
    withSearch,
    withCart,
    showRewardPoints,
    storeBasePath,
    showSendItemIcon,
    showTalkToSalesCta,
  } = useStoreContext();
  const { showSendToManyOption } = useSendItemsContext();
  const { isNewShopCreationFlow } = useCreateShopContext();
  const { isActivePackageUser } = useWorkspaceContext();

  const { isAuthenticated } = useAuth0V2();

  const showMenu = withMenu && isMobileNavOpen && !isFetchingMenu && !isSinglePageShop;

  const { loading } = useFetchExternalStore();

  useEffect(() => {
    // This useEffect ensures that the menu closes on route change
    setIsMobileNavOpen(false);
  }, [pathname]);

  const renderHomeLink = () => (
    <div className={styles.homeLinkBlock}>
      <Link href={storeBasePath} className={styles.homeLink} prefetch={false}>
        <div className={styles.iconWrapper}>
          <span className={styles.homeIcon} />
          <span className={styles.homeFontStyle}>Home</span>
        </div>
        <span className={styles.iconOuter} />
      </Link>
    </div>
  );

  const renderProfileSection = () => {
    if (isNewShopCreationFlow) return null;
    if (isAuthenticated) return <Profile id="profile" />;
  };

  return (
    <div
      style={{
        height: getResponsiveHeaderHeight(isMobileNavOpen, headerHeight),
        "--sticky-header-height": getResponsiveHeaderHeight(isMobileNavOpen, headerHeight),
      }}
    >
      <div
        className={`${styles.topNavBar} ${withMenu ? "" : styles.topNavWithoutMenu}`}
        ref={STICKY_HEADER_REF}
      >
        <div ref={FIRST_HEADER_REF}>
          <AppGenericAnnouncementBar application={SHOPS} options={{ isActivePackageUser }} />
          {isNewShopCreationFlow && (
            <ResponsiveHeaderContent level={1}>
              <CreateShopBanner />
            </ResponsiveHeaderContent>
          )}
          <ResponsiveHeaderContent className={styles.topNavBarLevelOne} level={2}>
            <div className={styles.navBarWithLogo}>
              <div className={styles.navContainer}>
                <BrandLogo />
                <div className={styles.navRightSection}>
                  <div className={styles.dropdownBlock}>
                    <CountrySelector />
                  </div>
                  {(withCart || (isPricelessShop && receivedItemsCount > 0)) && (
                    <CheckoutProvider skipPaymentOptions>
                      {withCart && <CartIcon />}
                      {isPricelessShop &&
                        receivedItemsCount > 0 &&
                        (loading ? <Spinner /> : <SelectedItemsCount />)}
                    </CheckoutProvider>
                  )}
                  <MobileMenuIcon
                    isMobileNavOpen={isMobileNavOpen}
                    setIsMobileNavOpen={setIsMobileNavOpen}
                  />
                </div>
              </div>
            </div>
          </ResponsiveHeaderContent>
        </div>
        {!isProductDetailPage && (
          <ResponsiveHeaderContent
            className={`${styles.responsiveSearchBar} ${isMobileNavOpen ? styles.hideElement : ""}`}
            level={3}
          >
            {withSearch && <GlobalProductSearch />}
          </ResponsiveHeaderContent>
        )}
        <ResponsiveHeaderContent className={styles.sliderNavigation} level={3}>
          <NavSlider isMobileNavOpen={isMobileNavOpen}>
            {isMobileNavOpen && (
              <BottomIconHideWrapper>
                <SliderContainer>
                  {workspaceSidebarFlagEnabled && (
                    <HeaderContent level={1} className={styles.profileSectionResponsive}>
                      {renderProfileSection()}
                    </HeaderContent>
                  )}
                  {showSendToManyOption && showSendItemIcon && (
                    <HeaderContent level={2}>
                      <SendItemsSelection onSave={() => setIsMobileNavOpen(false)} />
                    </HeaderContent>
                  )}
                  {showMenu && (
                    <HeaderContent level={3}>
                      <MultiLevelMenu />
                    </HeaderContent>
                  )}
                  <HeaderContent level={4}>{renderHomeLink()}</HeaderContent>
                  <HeaderContent level={5} className={styles.rewardsPoints}>
                    {" "}
                    {showRewardPoints && !isPricelessShop && (
                      <CheckoutProvider skipPaymentOptions>
                        <UserPointsBalance />
                      </CheckoutProvider>
                    )}
                  </HeaderContent>
                  {!workspaceSidebarFlagEnabled && (
                    <HeaderContent level={6} className={styles.profileSectionResponsive}>
                      {renderProfileSection()}
                    </HeaderContent>
                  )}
                  <div className={styles.bottomBlock}>
                    <HeaderContent level={7} className={styles.helpSection}>
                      <ExternalButton
                        styleType="link"
                        href={HELP_CENTER_URL}
                        className={styles.responsiveProfileHelpButton}
                        title="Help Center"
                        eventLocationAnalytics={{
                          track: true,
                          target: HEADER_ANALYTICS_TARGET.HELP_CENTER,
                        }}
                      >
                        <span className={styles.iconHelp}></span>
                        <span className={styles.profileHelpButtonText}>Help Center</span>
                      </ExternalButton>

                      {isAuthenticated ? (
                        <LogoutButton
                          className={styles.logoutButton}
                          eventLocationAnalytics={{
                            track: true,
                            target: HEADER_ANALYTICS_TARGET.LOGOUT,
                          }}
                        >
                          LOGOUT
                        </LogoutButton>
                      ) : (
                        <UnAuthtenticatedRightSection />
                      )}
                    </HeaderContent>
                    <HeaderContent className={styles.responsiveSliderButtonBlock} level={8}>
                      {showTalkToSalesCta && (
                        <TalkToSalesButton className={styles.button} target="_top" />
                      )}
                    </HeaderContent>
                  </div>
                </SliderContainer>
              </BottomIconHideWrapper>
            )}
          </NavSlider>
        </ResponsiveHeaderContent>
      </div>
    </div>
  );
};

export default ResponsiveMultiLevelHeader;
